import { ApiError } from "../api/apiClient";
import type { AddressType, PublicIpRiskResultDto, ScoreStatus } from "../types/dto";

// The one place that turns a full PublicIpRiskResultDto (or a failed lookup) into what a WebMCP tool
// hands back to an agent — every check_* tool in registerWebMcpTools.ts funnels through here so the
// two tools can never drift into two different result shapes or two different error vocabularies.
//
// Three rules this file exists to enforce (spec §十三-§十九):
//   1. netRiskScanIndex stays the ONLY score — no second 0-100 number is ever synthesized here.
//   2. `boolean | null` flags stay three-valued. `Boolean(value)` would read "unknown" as "false"; every
//      flag below is copied through as-is, never coerced.
//   3. Tor relay/exit stays two independent facts, never collapsed into "isRelay -> Exit".

export interface IpRiskToolResult {
  ipAddress: string;
  queriedAt: string;
  shareId: string;
  // One of AddressType — "public_routable" is the only value with a meaningful public-IP reputation;
  // every other value (private/loopback/reserved/...) is why risk.scoreStatus is "not_applicable".
  addressType: AddressType;
  isPubliclyRoutable: boolean;

  risk: {
    scoreStatus: ScoreStatus;
    // 0-100, higher = cleaner/lower risk. Null when no score exists this round — see `note`, which
    // always explains why rather than leaving the agent to guess from a bare null.
    netRiskScanIndex: number | null;
    indexBand: string | null;
    assessmentGrade: string | null;
    excellentEligible: boolean | null;
    serviceFriction: string | null;
    threatSeverity: string | null;
    keySignals: string[];
    // Plain-English reason there is no score (not_applicable / insufficient_data / partial), or null
    // when scoreStatus is "success" and netRiskScanIndex needs no caveat.
    note: string | null;
  };

  network: {
    // Best-estimate classification axes — see PublicRisk.networkClassification/connectionClassification.
    networkType: string | null;
    connectionType: string | null;
    anonymityProfile: string | null;
    asn: string | null;
    isp: string | null;
    organization: string | null;
    hostname: string | null;
    // Present only when this round recognized the address as shared public infrastructure (e.g. a
    // public DNS resolver) — spec §十七: this is why "datacenter" here must not read as "dangerous".
    recognizedInfrastructure: { type: string; organization: string | null } | null;
  };

  // detected / not_detected / unknown are all real, distinct answers — null means unknown, never
  // "not detected" (spec §十五).
  anonymity: {
    proxy: boolean | null;
    vpn: boolean | null;
    tor: boolean | null;
    relay: boolean | null;
  };

  classification: {
    hosting: boolean | null;
    datacenter: boolean | null;
    residential: boolean | null;
    mobile: boolean | null;
    bot: boolean | null;
    crawler: boolean | null;
    abuser: boolean | null;
    blacklisted: boolean | null;
  };

  location: {
    countryCode: string | null;
    countryName: string | null;
    regionName: string | null;
    cityName: string | null;
  };

  // Null when this round has no Tor Project evidence at all (unknown), never merely because the node
  // is not on the network — "not_tor" is itself a definite, reportable fact and gets its own object.
  tor: {
    nodeType: string;
    isRelay: boolean;
    isExit: boolean;
    isBadExit: boolean;
    role: string;
    exitPolicyLevel: string | null;
    allowedServiceCategories: string[] | null;
  } | null;
}

const ADDRESS_TYPE_DESCRIPTIONS: Partial<Record<AddressType, string>> = {
  unspecified: "an unspecified address (0.0.0.0 / ::)",
  loopback: "a loopback address",
  private: "a private network address (RFC 1918 / ULA)",
  carrier_grade_nat: "a carrier-grade NAT address",
  link_local: "a link-local address",
  documentation: "a documentation/example address",
  multicast: "a multicast address",
  reserved: "a reserved address",
  broadcast: "a broadcast address",
};

function buildScoreNote(scoreStatus: ScoreStatus, addressType: AddressType): string | null {
  if (scoreStatus === "not_applicable") {
    const description = ADDRESS_TYPE_DESCRIPTIONS[addressType] ?? "a special-purpose address";
    return `This is ${description}, not a public internet address, so NetRiskScan does not assign it a reputation score. Do not treat the missing score as a risk finding.`;
  }
  if (scoreStatus === "insufficient_data") {
    return "No NetRiskScan Index is available for this query — provider evidence was insufficient this round, not a sign of risk.";
  }
  if (scoreStatus === "partial") {
    return "The NetRiskScan Index is a real, final score, but this round's reputation/threat evidence coverage was incomplete.";
  }
  return null;
}

export function buildIpRiskToolResult(dto: PublicIpRiskResultDto): IpRiskToolResult {
  const { meta, risk, flags, network, location, torNetwork } = dto;

  return {
    ipAddress: meta.ipAddress,
    queriedAt: meta.queriedAt,
    shareId: dto.shareId,
    addressType: meta.addressType,
    isPubliclyRoutable: meta.isPubliclyRoutable,

    risk: {
      scoreStatus: risk.scoreStatus,
      netRiskScanIndex: risk.netRiskScanIndex,
      indexBand: risk.indexBand ?? null,
      assessmentGrade: risk.assessmentGrade ?? null,
      excellentEligible: risk.excellentEligible ?? null,
      serviceFriction: risk.serviceFriction ?? null,
      threatSeverity: risk.threatSeverity ?? null,
      keySignals: risk.keySignals,
      note: buildScoreNote(risk.scoreStatus, meta.addressType),
    },

    network: {
      networkType: risk.networkClassification?.type ?? null,
      connectionType: risk.connectionClassification?.type ?? null,
      anonymityProfile: risk.anonymityProfile ?? null,
      asn: network.asn,
      isp: network.isp,
      organization: network.organization,
      hostname: network.hostname,
      recognizedInfrastructure: risk.networkContext?.isRecognized
        ? { type: risk.networkContext.type, organization: risk.networkContext.organization ?? null }
        : null,
    },

    anonymity: {
      proxy: flags.isProxy,
      vpn: flags.isVpn,
      tor: flags.isTor,
      relay: flags.isRelay,
    },

    classification: {
      hosting: flags.isHosting,
      datacenter: flags.isDatacenter,
      residential: flags.isResidential,
      mobile: flags.isMobile,
      bot: flags.isBot,
      crawler: flags.isCrawler,
      abuser: flags.isAbuser,
      blacklisted: flags.isBlacklisted,
    },

    location: {
      countryCode: location.countryCode,
      countryName: location.countryName,
      regionName: location.regionName,
      cityName: location.cityName,
    },

    tor: torNetwork
      ? {
          nodeType: torNetwork.nodeType,
          isRelay: torNetwork.isRelay,
          isExit: torNetwork.isExit,
          isBadExit: torNetwork.isBadExit,
          role: torNetwork.role,
          exitPolicyLevel: torNetwork.exitPolicyLevel,
          allowedServiceCategories: torNetwork.allowedServiceCategories,
        }
      : null,
  };
}

export type WebMcpToolErrorCode =
  | "invalid_input"
  | "rate_limited"
  | "quota_exhausted"
  | "temporarily_unavailable"
  | "upstream_error";

export interface WebMcpToolErrorPayload {
  code: WebMcpToolErrorCode;
  message: string;
  status?: number;
  traceId?: string;
}

export type WebMcpToolExecuteResult =
  | { success: true; result: IpRiskToolResult }
  | { success: false; error: WebMcpToolErrorPayload };

// Server error codes (NetRiskScan.Domain.Enums.IpRiskErrorCodes) that map to something more specific
// than a plain "upstream_error" — everything else (provider auth/response/data problems, unrecognized
// codes) falls through to that generic bucket rather than pretending to know more than the frontend does.
const RATE_LIMIT_CODES = new Set(["CLIENT_RATE_LIMITED"]);
const QUOTA_CODES = new Set([
  "CLIENT_DAILY_LIMIT_EXCEEDED",
  "CLIENT_MONTHLY_LIMIT_EXCEEDED",
  "ANONYMOUS_DAILY_LIMIT_EXCEEDED",
]);
const UNAVAILABLE_CODES = new Set([
  "PROVIDER_TIMEOUT",
  "PROVIDER_CIRCUIT_OPEN",
  "PROVIDER_RATE_LIMITED",
  "PROVIDER_QUOTA_EXCEEDED",
  "ALL_PROVIDERS_FAILED",
  "CACHE_ERROR",
]);

function classifyApiError(error: ApiError): WebMcpToolErrorCode {
  const code = error.code ?? "";
  if (code === "INVALID_IP") return "invalid_input";
  if (RATE_LIMIT_CODES.has(code)) return "rate_limited";
  if (QUOTA_CODES.has(code)) return "quota_exhausted";
  if (UNAVAILABLE_CODES.has(code)) return "temporarily_unavailable";
  if (error.status === 429) return "rate_limited";
  return "upstream_error";
}

// A validation failure caught locally, before apiClient is ever called — never carries a status or
// traceId, since no request was made (spec §二十一: a typo must never cost the caller a query).
export function buildValidationErrorResult(message: string): WebMcpToolExecuteResult {
  return { success: false, error: { code: "invalid_input", message } };
}

// Never forwards `error.stack`, provider names, internal URLs, or anything else ApiError/apiClient
// doesn't already surface on its own public-safe `message` (spec §十九) — only code/message/status/
// traceId, the same fields the website's own error banner already renders from an ApiError.
export function buildToolErrorResult(error: unknown): WebMcpToolExecuteResult {
  if (error instanceof ApiError) {
    return {
      success: false,
      error: {
        code: classifyApiError(error),
        message: error.message,
        status: error.status,
        traceId: error.traceId,
      },
    };
  }
  return {
    success: false,
    error: { code: "upstream_error", message: "The NetRiskScan lookup failed unexpectedly." },
  };
}
